/**
 * Pure world-units <-> canvas-pixels projection for the World viewer's 2D
 * map pane — no `CanvasRenderingContext2D`, no DOM, so pan/zoom/hit-testing
 * math is unit-testable without a canvas. `world/world-canvas.ts` is the one
 * imperative caller.
 *
 * Fits the region's placement bounds into the viewport (uniform scale, no
 * stretch), Y-flipped so increasing world Y draws upward, then applies a
 * zoom multiplier and a screen-pixel pan offset on top.
 *
 * Which world axis is "north" is still UNKNOWN (`world/placement-transform.ts`),
 * but the Y flip here is no longer arbitrary: it is what makes this pane agree
 * with the 3D one beside it. That module sends `.WLD` `y` to three's `-Z`, so
 * a camera looking down at the ground plane also puts increasing `y` up-screen.
 * Change one of the two and the region reads mirrored between the panes.
 */

export interface WorldBoundsLike {
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export interface PanZoom {
  readonly zoom: number;
  /** Screen-pixel offset of the projection's centre from the viewport's centre. */
  readonly panX: number;
  readonly panY: number;
}

export const DEFAULT_PAN_ZOOM: PanZoom = { zoom: 1, panX: 0, panY: 0 };
const DEFAULT_PADDING_FRACTION = 0.08;

function safeSpan(span: number): number {
  return span > 0 ? span : 1;
}

/** World-units-per-pixel scale that fits `bounds` inside `viewport` with `paddingFraction` margin on every side, before `panZoom.zoom` is applied. */
export function fitScale(bounds: WorldBoundsLike, viewport: Viewport, paddingFraction: number = DEFAULT_PADDING_FRACTION): number {
  const availWidth = viewport.width * (1 - 2 * paddingFraction);
  const availHeight = viewport.height * (1 - 2 * paddingFraction);
  const boundsWidth = safeSpan(bounds.maxX - bounds.minX);
  const boundsHeight = safeSpan(bounds.maxY - bounds.minY);
  return Math.min(availWidth / boundsWidth, availHeight / boundsHeight);
}

export interface ScreenPoint {
  readonly x: number;
  readonly y: number;
}

export interface WorldPoint {
  readonly x: number;
  readonly y: number;
}

export interface MapProjection {
  /** Effective world-units-per-pixel scale (`fitScale(...) * panZoom.zoom`). */
  readonly scale: number;
  toScreen(world: WorldPoint): ScreenPoint;
  toWorld(screen: ScreenPoint): WorldPoint;
}

/** Builds a `{ toScreen, toWorld }` pair over one fixed bounds/viewport/pan-zoom snapshot — exact inverses of each other. */
export function createMapProjection(
  bounds: WorldBoundsLike,
  viewport: Viewport,
  panZoom: PanZoom = DEFAULT_PAN_ZOOM,
  paddingFraction: number = DEFAULT_PADDING_FRACTION,
): MapProjection {
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const scale = fitScale(bounds, viewport, paddingFraction) * panZoom.zoom;
  const screenCenterX = viewport.width / 2 + panZoom.panX;
  const screenCenterY = viewport.height / 2 + panZoom.panY;

  return {
    scale,
    toScreen: (world) => ({
      x: screenCenterX + (world.x - centerX) * scale,
      y: screenCenterY - (world.y - centerY) * scale,
    }),
    toWorld: (screen) => ({
      x: centerX + (screen.x - screenCenterX) / scale,
      y: centerY - (screen.y - screenCenterY) / scale,
    }),
  };
}

export interface WorldIndexedPoint extends WorldPoint {
  readonly index: number;
}

/** The closest of `points` to `target`, or `undefined` if none is within `maxWorldDistance` (or `points` is empty). */
export function pickNearestPoint(
  target: WorldPoint,
  points: readonly WorldIndexedPoint[],
  maxWorldDistance: number,
): WorldIndexedPoint | undefined {
  let best: WorldIndexedPoint | undefined;
  let bestDistance = maxWorldDistance;
  for (const point of points) {
    const distance = Math.hypot(point.x - target.x, point.y - target.y);
    if (distance <= bestDistance) {
      best = point;
      bestDistance = distance;
    }
  }
  return best;
}
